import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  CheckoutOptimizationWidgetType,
  ManualProductSourceSchema_,
  PurchaseCheckoutBlockRenderTarget,
  ShippingOptionRenderTarget,
} from "./widget";

/**
 * Base schema for all checkout widgets in the collection
 */
const checkoutWidgetBaseSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  name: z.string(),
  type: createEnum(CheckoutOptimizationWidgetType),
  createdAt: z.date(),
  updatedAt: z.date(),
});

const baseStandardWidgetSchema = checkoutWidgetBaseSchema.extend({
  enabledGlobally: z.boolean(),
  target: createEnum(PurchaseCheckoutBlockRenderTarget),
});

/**
 * Fulfillment Cutoff Widget schema
 */
const fulfillmentCutoffWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF),
  text: z.string(),
  backgroundColor: z.string(),
  iconColor: z.string(),
});

export type FulfillmentCutoffWidget = z.infer<
  typeof fulfillmentCutoffWidgetSchema
>;

/**
 * Threshold Upsell Widget schema
 */
const thresholdUpsellWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.THRESHOLD_UPSELL),
  threshold: z.object({ amount: z.number(), currency: createEnum(Currency) }),
  gteThresholdText: z.string(),
  ltThresholdText: z.string(),
  addToCartButtonText: z.string(),
  addToCartButtonTextColor: z.string().optional(),
  addToCartButtonColor: z.string().optional(),
  progressBarColor: z
    .object({ filled: z.string().optional(), unfilled: z.string().optional() })
    .optional(),
  excludedProductTags: z.array(z.string()).optional(),
  fallbackProductTags: z.array(z.string()).optional(),
  manualProductSources: z.array(ManualProductSourceSchema_).optional(),
});

export type ThresholdUpsellWidget = z.infer<typeof thresholdUpsellWidgetSchema>;

/**
 * Content Block Widget schema
 */
const contentBlockWidgetSchema = baseStandardWidgetSchema.extend({
  type: z.literal(CheckoutOptimizationWidgetType.CONTENT_BLOCK),
  sections: z
    .array(
      z.object({
        _id: z.string(),
        title: z.string(),
        description: z.string(),
        imageUrl: z.string().nullish(),
      }),
    )
    .max(5),
});

export type ContentBlockWidget = z.infer<typeof contentBlockWidgetSchema>;

/**
 * Shipping Option Content Widget schema
 * Renders within shipping option items in checkout
 */
export const shippingOptionContentWidgetSchema =
  checkoutWidgetBaseSchema.extend({
    type: z.literal(CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT),
    target: createEnum(ShippingOptionRenderTarget),
    title: z.string(),
    description: z.string(),
    imageUrl: z.string().nullish(),
    backgroundColor: z.string(),
  });
export interface ShippingOptionContentWidget
  extends z.infer<typeof shippingOptionContentWidgetSchema> {}

/**
 * Discriminated union of all checkout widget types
 */
export const checkoutWidgetSchema = z.discriminatedUnion("type", [
  fulfillmentCutoffWidgetSchema,
  thresholdUpsellWidgetSchema,
  contentBlockWidgetSchema,
  shippingOptionContentWidgetSchema,
]);

export type CheckoutWidget = z.infer<typeof checkoutWidgetSchema>;

export const checkoutWidgetWithAssignmentsSchema = z.intersection(
  checkoutWidgetSchema,
  z.object({
    assignedExperienceIds: z.array(zExt.objectId()),
    assignedExperienceNames: z.array(z.string()),
  }),
);

export type CheckoutWidgetWithAssignments = z.infer<
  typeof checkoutWidgetWithAssignmentsSchema
>;
